(() => {
'use strict';
const ldzArticleReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const ldzArticleClamp = (value, min = 0, max = 1) => Math.min(Math.max(value, min), max);

class LDZArticle extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.abortController = new AbortController();
    this.progressBar = this.querySelector('[data-article-progress]');
    this.articleStart = this.querySelector('[data-article-start]');
    this.scrollButton = this.querySelector('[data-article-scroll]');
    this.shareButton = this.querySelector('[data-share-native]');
    this.copyButton = this.querySelector('[data-share-copy]');
    this.copyLabel = this.querySelector('[data-copy-label]');
    this.rafPending = false;
    this.rafId = 0;

    this.setupActions();
    this.setupTables();
    this.setupContents();
    this.setupReveal();
    this.requestUpdate();
    this.readyFrame = requestAnimationFrame(() => { if (this.isConnected) this.classList.add('is-ready'); });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.unsubscribeProgress?.();
    this.revealObserver?.disconnect();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.rafId);
    cancelAnimationFrame(this.readyFrame);
    clearTimeout(this.revealDeadline);
    clearTimeout(this.copyTimer);
    this.querySelectorAll('[data-article-reveal]').forEach((node) => node.classList.add('is-visible'));
    this.dataset.initialized = 'false';
  }

  setupActions() {
    if (this.scrollButton && this.articleStart) {
      this.scrollButton.addEventListener(
        'click',
        () => {
          this.articleStart.focus({ preventScroll: true });
          this.articleStart.scrollIntoView({ behavior: ldzArticleReduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
        },
        { signal: this.abortController.signal }
      );
    }

    if (this.shareButton) {
      if (!navigator.share) this.shareButton.hidden = true;
      else this.shareButton.addEventListener('click', () => this.shareArticle(), { signal: this.abortController.signal });
    }

    this.copyButton?.addEventListener('click', () => this.copyArticleLink(), { signal: this.abortController.signal });

    const schedulerKey = `article-${this.dataset.sectionId || Math.random().toString(36).slice(2)}`;
    if (window.DivettaZorge?.subscribe) this.unsubscribeProgress = window.DivettaZorge.subscribe(schedulerKey, () => this.updateProgress());
    else window.addEventListener('scroll', () => this.requestUpdate(), {
      passive: true,
      signal: this.abortController.signal
    });
    window.addEventListener('resize', () => this.requestUpdate(), {
      passive: true,
      signal: this.abortController.signal
    });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.requestUpdate());
      this.resizeObserver.observe(this);
    }
  }

  setupReveal() {
    const nodes = Array.from(this.querySelectorAll('[data-article-reveal]'));
    if (ldzArticleReduceMotion.matches || !('IntersectionObserver' in window)) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return;
    }

    const threshold = 0;
    const effectiveViewportHeight = (window.innerHeight || document.documentElement.clientHeight) * 0.9;
    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this.revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin: '0px 0px 64px 0px' }
    );
    nodes.forEach((node) => {
      // A block taller than the viewport/threshold ratio can never reach 15%.
      // Keep long-form article content readable instead of leaving it permanently hidden.
      if (node.getBoundingClientRect().top < effectiveViewportHeight || window.Shopify?.designMode) {
        node.classList.add('is-visible');
        return;
      }
      this.revealObserver.observe(node);
    });
    const revealAll = () => { nodes.forEach((node) => node.classList.add('is-visible')); this.revealObserver?.disconnect(); };
    this.revealDeadline = setTimeout(revealAll, 6000);
    ldzArticleReduceMotion.addEventListener?.('change', () => { if (ldzArticleReduceMotion.matches) revealAll(); }, { signal: this.abortController.signal });
  }

  shareUrl() {
    const canonical = document.querySelector('link[rel="canonical"]')?.href;
    try {
      const url = new URL(canonical || location.href);
      ['preview_theme_id', '_fd', 'pb'].forEach((key) => url.searchParams.delete(key));
      return url.href;
    } catch (_) { return location.href; }
  }

  setupContents() {
    const content = this.querySelector('[data-article-content]');
    if (!content || this.querySelector('[data-article-toc]')) return;
    // Published Journal bodies already contain authored navigation. Keep that owner.
    const authored = Array.from(content.querySelectorAll('nav')).find((nav) => {
      const targets = new Set();
      nav.querySelectorAll('a[href^="#"]').forEach((link) => {
        try {
          const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
          if (target && content.contains(target) && /^H[2-6]$/.test(target.tagName)) targets.add(target);
        } catch (_) { /* Ignore malformed anchors; never replace article text. */ }
      });
      return targets.size >= 2;
    });
    if (authored) {
      authored.dataset.articleToc = 'authored';
      if (!authored.hasAttribute('aria-label')) authored.setAttribute('aria-label', document.documentElement.lang.toLowerCase().startsWith('it') ? 'Indice della guida' : 'Guide contents');
      return;
    }
    const headings = Array.from(content.querySelectorAll('h2')).filter((heading) => heading.textContent.trim());
    if (headings.length < 3) return;
    const nav = document.createElement('nav');
    nav.className = 'ldz-article-toc';
    nav.dataset.articleToc = '';
    const italian = document.documentElement.lang.toLowerCase().startsWith('it');
    nav.setAttribute('aria-label', italian ? 'Indice della guida' : 'Guide contents');
    const label = document.createElement('p');
    label.textContent = italian ? 'In questa guida' : 'In this guide';
    const list = document.createElement('ol');
    headings.forEach((heading, index) => {
      if (!heading.id) {
        const base = `ld-guide-${this.dataset.sectionId || 'article'}-${index + 1}`;
        let id = base, suffix = 1;
        while (document.getElementById(id)) id = `${base}-${suffix++}`;
        heading.id = id;
      }
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      item.append(link); list.append(item);
    });
    nav.append(label, list); content.prepend(nav);
  }

  setupTables() {
    this.querySelectorAll('[data-article-content] table').forEach((table) => {
      if (table.parentElement?.classList.contains('ldz-article-table-scroll')) return;
      const wrapper = document.createElement('div');
      const caption = table.querySelector('caption')?.textContent?.trim();
      wrapper.className = 'ldz-article-table-scroll';
      wrapper.setAttribute('role', 'region');
      wrapper.setAttribute('aria-label', caption || 'Tabella dell’articolo');
      wrapper.tabIndex = 0;
      table.before(wrapper);
      wrapper.append(table);
    });
  }

  requestUpdate() {
    if (this.rafPending) return;
    this.rafPending = true;
    this.rafId = requestAnimationFrame(() => {
      if (!this.isConnected) { this.rafPending = false; return; }
      this.updateProgress();
      this.rafPending = false;
    });
  }

  updateProgress() {
    if (!this.progressBar) return;
    const rect = this.getBoundingClientRect();
    const viewport = window.innerHeight || document.documentElement.clientHeight;
    const totalScrollable = Math.max(this.offsetHeight - viewport, 1);
    const scrolled = Math.min(Math.max(-rect.top, 0), totalScrollable);
    this.progressBar.style.transform = `scaleX(${ldzArticleClamp(scrolled / totalScrollable)})`;
  }

  async shareArticle() {
    try {
      await navigator.share({
        title: document.title,
        text: document.querySelector('meta[name="description"]')?.content || '',
        url: this.shareUrl()
      });
    } catch (error) {
      if (error.name !== 'AbortError') await this.copyArticleLink();
    }
  }

  async copyArticleLink() {
    const original = this.copyLabel?.dataset.originalLabel || this.copyLabel?.textContent || 'Copia link';
    if (this.copyLabel) this.copyLabel.dataset.originalLabel = original;
    let copied = false;
    try {
      await navigator.clipboard.writeText(this.shareUrl());
      copied = true;
    } catch (error) {
      const temporary = document.createElement('textarea');
      temporary.value = this.shareUrl();
      temporary.setAttribute('readonly', '');
      temporary.style.position = 'fixed';
      temporary.style.opacity = '0';
      document.body.append(temporary);
      temporary.select();
      try { copied = document.execCommand('copy'); } catch (_) { copied = false; }
      finally { temporary.remove(); }
    }

    if (this.copyLabel) this.copyLabel.textContent = copied ? 'Link copiato' : 'Copia non riuscita';
    clearTimeout(this.copyTimer);
    this.copyTimer = window.setTimeout(() => {
      if (this.copyLabel) this.copyLabel.textContent = original;
    }, 1800);
  }
}

if (!customElements.get('ld-zorge-article')) customElements.define('ld-zorge-article', LDZArticle);

})();
